/**
 * AI Developer Training Day 1
 * 
 * ENGLISH VERSION using standardized design system
 * All slides use consistent layouts, spacing, and typography
 */

import type { PresentationData } from '../types'
import {
  StandardTitleSlide,
  StandardContentSlide,
  StandardGridSlide,
  FeatureCard,
  InfoBox,
  AgendaItem,
} from '../components/slides'
import { IconSolid } from '../components/ui/Icon'

export const developerTrainingDay1: PresentationData = {
  metadata: {
    title: 'AI Developer Training Day 1',
    description: 'AI Fundamentals & AI Coding Tools for Developers',
    author: 'AI Experts & Development Consultants',
    date: 'October 2025',
    tags: ['AI', 'AI Tools', 'Development', 'Workshop'],
  },
  slides: [
    // ============================================
    // SLIDE 1: Title
    // ============================================
    {
      id: 1,
      component: () => (
        <StandardTitleSlide
          title="AI Developer Training Day 1"
          subtitle="AI Fundamentals & AI Coding Tools for Developers"
          author="AI Experts & Development Consultants"
          date="October 2025"
          tags={['8 Hours Training', 'All Levels', 'Hands-on']}
          background="primary"
          footer={
            <div className="flex flex-wrap justify-center gap-6 text-white pb-4">
              <div className="text-center">
                <IconSolid name="clock" size="lg" className="mx-auto mb-1" />
                <p className="text-sm font-semibold">Full Day</p>
              </div>
              <div className="text-center">
                <IconSolid name="graduation-cap" size="lg" className="mx-auto mb-1" />
                <p className="text-sm font-semibold">Interactive</p>
              </div>
              <div className="text-center">
                <IconSolid name="laptop-code" size="lg" className="mx-auto mb-1" />
                <p className="text-sm font-semibold">Practical</p>
              </div>
            </div>
          }
        />
      ),
      transition: 'fade',
    },

    // ============================================
    // SLIDE 2: Agenda
    // ============================================
    {
      id: 2,
      component: () => (
        <StandardGridSlide
          title="Day Agenda - Day 1"
          subtitle="From AI Fundamentals to Practical Coding Tools"
          columns={2}
          background="secondary"
          items={[
            <AgendaItem
              icon={<IconSolid name="brain" />}
              title="Block 1: AI Fundamentals"
              duration="09:00 - 10:45"
              items={[
                'Understanding AI Landscape (ML, DL, GenAI)',
                'Large Language Models Deep Dive',
                'Prompt Engineering Essentials',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="tools" />}
              title="Block 2: AI Coding Tools"
              duration="10:45 - 12:30"
              items={[
                'Tool Matrix & Comparison',
                'GitHub Copilot, Cursor, Claude, Cline',
                'Live Demos & Use Cases',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="code" />}
              title="Block 3: Hands-on Copilot"
              duration="13:15 - 14:30"
              items={[
                'GitHub Copilot Setup & Configuration',
                'Core Workflows: Completion, Chat, TDD',
                'Workshop: E-Commerce Product API',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="rocket" />}
              title="Block 4: Practice & Security"
              duration="14:45 - 16:00"
              items={[
                'Workshop: Legacy Code Modernization',
                'Workshop: Increase Test Coverage',
                'Security Best Practices',
              ]}
            />,
            <AgendaItem
              icon={<IconSolid name="bullseye" />}
              title="Block 5: Context Engineering"
              duration="16:00 - 16:45"
              items={[
                'Systematic AI Development',
                'Framework & Best Practices',
                'Practical Exercise',
              ]}
            />,
          ]}
          footer={
            <InfoBox variant="tip">
              <strong>Goal:</strong> After this day, you'll be able to effectively use AI coding tools, apply context engineering, and significantly increase your development productivity
            </InfoBox>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // BLOCK 1: AI FUNDAMENTALS
    // ============================================

    // SLIDE 3: Section Divider - Block 1
    {
      id: 3,
      component: () => (
        <StandardContentSlide
          title="Block 1"
          subtitle="AI Fundamentals"
          background="primary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <IconSolid name="brain" className="w-32 h-32 text-white opacity-90" />
            <div className="text-center space-y-4">
              <h2 className="text-5xl font-bold text-white">Understanding AI</h2>
              <p className="text-2xl text-white/80 max-w-3xl mx-auto">
                From AI to ML to Generative AI - The foundation you need
              </p>
              <div className="flex gap-6 justify-center pt-4 text-white/70">
                <div className="flex items-center gap-2">
                  <IconSolid name="clock" />
                  <span>105 Minutes</span>
                </div>
                <div className="flex items-center gap-2">
                  <IconSolid name="book-open" />
                  <span>Theory & Practice</span>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 4: AI Landscape - Theory
    {
      id: 4,
      component: () => (
        <StandardGridSlide
          title="1.1 Understanding the AI Landscape"
          subtitle="From AI to ML to Generative AI - The Theory"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="brain" size="2xl" className="text-primary-600" />}
              title="Artificial Intelligence (AI)"
              description="The umbrella term"
              items={[
                'Simulation of human intelligence by machines',
                'Learning systems for complex tasks',
                'Applications: Speech recognition, image recognition, decision making',
                'First concepts: 1950s (Alan Turing)',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="project-diagram" size="2xl" className="text-secondary-600" />}
              title="Machine Learning (ML)"
              description="Learning algorithms"
              items={[
                'Systems learn from data without explicit programming',
                'Supervised, Unsupervised, Reinforcement Learning',
                'Examples: Spam filters, recommendation systems, prediction models',
                'Breakthrough: 2000s with Big Data',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="network-wired" size="2xl" className="text-accent-600" />}
              title="Deep Learning (DL)"
              description="Neural networks"
              items={[
                'Subset of ML with multi-layered neural networks',
                'Recognizes complex patterns in large datasets',
                'Computer Vision, Language Processing, Autonomous Driving',
                'Revolution: 2012 with ImageNet success',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="magic" size="2xl" className="text-purple-600" />}
              title="Generative AI"
              description="The latest evolution"
              items={[
                'Creates new content: Text, code, images, music',
                'Basis: Large Language Models (LLMs)',
                'ChatGPT, GitHub Copilot, DALL-E, Midjourney',
                'Mainstream: November 2022 with ChatGPT',
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 5: AI Landscape - Evolution Timeline
    {
      id: 5,
      component: () => (
        <StandardContentSlide
          title="The Evolution of AI"
          subtitle="From rule-based systems to Generative AI"
          background="light"
        >
          <div className="space-y-8">
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                <div className="text-3xl font-bold text-blue-900 mb-2">1950s-1980s</div>
                <div className="text-lg font-semibold text-blue-800 mb-3">Expert Systems</div>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Rule-based AI</li>
                  <li>• If-Then logic</li>
                  <li>• Limited scope</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                <div className="text-3xl font-bold text-green-900 mb-2">1990s-2000s</div>
                <div className="text-lg font-semibold text-green-800 mb-3">Machine Learning</div>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Data-driven</li>
                  <li>• Pattern recognition</li>
                  <li>• Statistical models</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                <div className="text-3xl font-bold text-purple-900 mb-2">2010s</div>
                <div className="text-lg font-semibold text-purple-800 mb-3">Deep Learning</div>
                <ul className="text-sm text-purple-700 space-y-1">
                  <li>• Neural networks</li>
                  <li>• ImageNet success</li>
                  <li>• AlphaGo wins</li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl border border-orange-200">
                <div className="text-3xl font-bold text-orange-900 mb-2">2020s</div>
                <div className="text-lg font-semibold text-orange-800 mb-3">Generative AI</div>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• GPT-3, ChatGPT</li>
                  <li>• Content creation</li>
                  <li>• Mainstream adoption</li>
                </ul>
              </div>
            </div>

            <InfoBox variant="info">
              <strong>Key Insight:</strong> Each evolution built upon the previous one. Generative AI combines all previous innovations with massive scale and compute power.
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 6: AI Landscape - Interactive Discussion
    {
      id: 6,
      component: () => (
        <StandardContentSlide
          title="💭 Interactive Discussion"
          subtitle="Where are YOU already using AI?"
          background="secondary"
        >
          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <FeatureCard
                icon={<IconSolid name="comments" className="text-blue-400" />}
                title="Daily AI Usage"
                description="Let's collect examples"
                items={[
                  'Search engines (Google, Bing)',
                  'Autocomplete & suggestions',
                  'Virtual assistants (Siri, Alexa)',
                  'Photo organization & filters',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="code" className="text-green-400" />}
                title="Development Tools"
                description="AI in your workflow"
                items={[
                  'Code completion (IntelliSense)',
                  'GitHub Copilot / similar',
                  'ChatGPT for debugging',
                  'AI-powered search (Stack Overflow)',
                ]}
                variant="glass"
              />
            </div>

            <InfoBox variant="warning">
              <strong>Discussion (5 min):</strong> Share your experiences - Which AI tools do you already use in your daily work? What works well, what doesn't?
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 7: Practical Exercise - AI Categorization
    {
      id: 7,
      component: () => (
        <StandardContentSlide
          title="✏️ Practical Exercise 1"
          subtitle="AI Categorization (10 Minutes)"
          background="primary"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-3">📝 Task</h3>
              <p className="mb-4">
                Use ChatGPT/Claude to categorize these tools: Which are AI, ML, DL, or Generative AI?
              </p>
            </InfoBox>

            <div className="grid grid-cols-3 gap-4">
              <FeatureCard
                title="Tools to Categorize"
                items={[
                  'Netflix Recommendations',
                  'Tesla Autopilot',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="More Tools"
                items={[
                  'Grammarly',
                  'Google Translate',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Even More"
                items={[
                  'Midjourney',
                  'GitHub Copilot',
                ]}
                variant="glass"
              />
            </div>

            <InfoBox variant="tip">
              <strong>Example Prompt:</strong> "Categorize the following tools into AI, ML, Deep Learning, or Generative AI. Explain your reasoning for each: Netflix Recommendations, Tesla Autopilot, Grammarly, Google Translate, Midjourney, GitHub Copilot"
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 8: LLM Deep Dive - Theory
    {
      id: 8,
      component: () => (
        <StandardContentSlide
          title="1.2 Large Language Models - Deep Dive"
          subtitle="How do GPT, Claude & Co work?"
          background="light"
        >
          {/* Architecture Flow */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="text-center">
              <div className="bg-blue-100 p-6 rounded-xl mb-2">
                <IconSolid name="keyboard" size="2xl" className="text-blue-600" />
              </div>
              <p className="text-sm font-semibold">Input Text</p>
            </div>
            
            <IconSolid name="arrow-right" size="2xl" className="text-gray-400" />
            
            <div className="text-center">
              <div className="bg-green-100 p-6 rounded-xl mb-2">
                <IconSolid name="puzzle-piece" size="2xl" className="text-green-600" />
              </div>
              <p className="text-sm font-semibold">Tokenization</p>
            </div>
            
            <IconSolid name="arrow-right" size="2xl" className="text-gray-400" />
            
            <div className="text-center">
              <div className="bg-purple-100 p-6 rounded-xl mb-2">
                <IconSolid name="brain" size="2xl" className="text-purple-600" />
              </div>
              <p className="text-sm font-semibold">Processing</p>
            </div>
            
            <IconSolid name="arrow-right" size="2xl" className="text-gray-400" />
            
            <div className="text-center">
              <div className="bg-orange-100 p-6 rounded-xl mb-2">
                <IconSolid name="file-alt" size="2xl" className="text-orange-600" />
              </div>
              <p className="text-sm font-semibold">Output</p>
            </div>
          </div>

          <InfoBox variant="info" className="mb-6">
            <strong>How it works:</strong>
            <ul className="mt-2 space-y-1">
              <li>1. <strong>Input:</strong> Your text/question</li>
              <li>2. <strong>Tokenization:</strong> Text split into small units (tokens)</li>
              <li>3. <strong>Processing:</strong> Transformer model processes tokens with "attention mechanism"</li>
              <li>4. <strong>Prediction:</strong> Model predicts most likely next token</li>
              <li>5. <strong>Generation:</strong> Repeats until complete response</li>
            </ul>
          </InfoBox>

          <div className="grid grid-cols-3 gap-4">
            <FeatureCard
              icon={<IconSolid name="layer-group" size="xl" />}
              title="Transformer Architecture"
              description="Attention mechanism for parallel processing"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="puzzle-piece" size="xl" />}
              title="Token-Based"
              description="Text split into small units (tokens)"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="brain" size="xl" />}
              title="Probabilistic"
              description="Prediction of most likely next token"
              variant="elevated"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 9: LLM - Technical Details
    {
      id: 9,
      component: () => (
        <StandardGridSlide
          title="LLM - Technical Details"
          subtitle="Parameters, Context Windows & Training"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="database" className="text-blue-600" />}
              title="Model Parameters"
              description="Size determines capabilities"
              items={[
                'GPT-3.5: 175 billion parameters',
                'GPT-4: ~1.7 trillion parameters (estimated)',
                'Claude 3 Opus: ~300-500B parameters',
                'Bigger ≠ always better (cost, speed)',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="window-maximize" className="text-green-600" />}
              title="Context Windows"
              description='How much can the model "see"?'
              items={[
                'GPT-3.5: 4K-16K tokens (~3K-12K words)',
                'GPT-4: 8K-128K tokens',
                'Claude 3: 200K tokens (~150K words)',
                'Gemini 1.5: 1M tokens (entire codebases!)',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="graduation-cap" className="text-purple-600" />}
              title="Training Process"
              description="How LLMs learn"
              items={[
                'Pre-Training: Billions of texts from internet',
                'Fine-Tuning: Specific tasks & data',
                'RLHF: Reinforcement Learning from Human Feedback',
                'Cost: Millions to billions of dollars',
              ]}
            />,
            <FeatureCard
              icon={<IconSolid name="bolt" className="text-yellow-600" />}
              title="Inference vs Training"
              description="Understanding the difference"
              items={[
                'Training: One-time, very expensive, months',
                'Inference: Each request, cheap, seconds',
                'We use: Inference (API calls)',
                'Training: Only relevant for custom models',
              ]}
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 10: LLM - Interactive Demo
    {
      id: 10,
      component: () => (
        <StandardContentSlide
          title="💭 Live Demo: LLM in Action"
          subtitle="Let's try it together"
          background="secondary"
        >
          <div className="space-y-4">
            <InfoBox variant="warning">
              <strong>Live Demo (10 min):</strong> We'll demonstrate how different prompts lead to different results
            </InfoBox>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <IconSolid name="times-circle" className="text-red-400" />
                  Bad Prompt
                </h3>
                <div className="bg-red-900/30 p-4 rounded-lg mb-4">
                  <code className="text-red-200 text-sm">
                    "Write me a function"
                  </code>
                </div>
                <ul className="text-white/70 space-y-2 text-sm">
                  <li>❌ No context</li>
                  <li>❌ Unclear requirements</li>
                  <li>❌ Unpredictable result</li>
                </ul>
              </div>

              <div className="bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <IconSolid name="check-circle" className="text-green-400" />
                  Good Prompt
                </h3>
                <div className="bg-green-900/30 p-4 rounded-lg mb-4">
                  <code className="text-green-200 text-sm">
                    "Create a TypeScript function that validates email addresses using regex. Include error handling and unit tests."
                  </code>
                </div>
                <ul className="text-white/70 space-y-2 text-sm">
                  <li>✅ Clear task</li>
                  <li>✅ Specified language</li>
                  <li>✅ Requirements included</li>
                </ul>
              </div>
            </div>

            <div className="text-center text-white/60 text-sm pt-4">
              We'll try both prompts in ChatGPT and compare results
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // Continue with remaining slides...
    // Due to length, I'll show the pattern for a few more key slides

    // SLIDE 12: Prompt Engineering - Theory
    {
      id: 12,
      component: () => (
        <StandardGridSlide
          title="1.3 Prompt Engineering Essentials"
          subtitle="From bad to perfect prompts"
          columns={2}
          background="primary"
          items={[
            <FeatureCard
              icon={<IconSolid name="times-circle" size="2xl" className="text-red-400" />}
              title="❌ Bad Prompts"
              description="Vague, ambiguous, no context"
              items={[
                '"Write me a function"',
                '"Make it better"',
                '"Create an API"',
                '"Fix the bug"',
              ]}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" size="2xl" className="text-green-400" />}
              title="✅ Good Prompts"
              description="Specific, structured, with context"
              items={[
                'Clear task description',
                'Relevant context (language, framework)',
                'Desired format/style',
                'Constraints and requirements',
              ]}
              variant="glass"
            />,
          ]}
          footer={
            <InfoBox variant="tip">
              <strong>Golden Rule:</strong> The more precise the prompt, the better the result. Invest time in good prompts!
            </InfoBox>
          }
        />
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 11: LLM - Practical Exercise
    // ============================================
    {
      id: 11,
      component: () => (
        <StandardContentSlide
          title="✏️ Practical Exercise 2"
          subtitle="Using LLM for Code Tasks"
          background="primary"
        >
          <div className="space-y-4">
            <InfoBox variant="warning">
              <h3 className="text-lg font-bold mb-2">📝 Task (15 Minutes)</h3>
              <p className="text-sm">
                Use ChatGPT or Claude for these coding challenges:
              </p>
            </InfoBox>

            <div className="grid grid-cols-3 gap-3">
              <FeatureCard
                title="Challenge 1"
                description="⭐ Beginner"
                items={[
                  'Array of User objects',
                  'Group by Role',
                  'Sort by Name',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Challenge 2"
                description="⭐⭐ Advanced"
                items={[
                  'TypeScript HTTP Client',
                  'Error Handling',
                  'Retry Logic',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Challenge 3"
                description="⭐⭐⭐ Expert"
                items={[
                  'LRU Cache O(1)',
                  'TypeScript Generics',
                  'Unit Tests',
                ]}
                variant="glass"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-900 p-3 rounded-lg">
                <p className="text-sm text-green-400 mb-1">Evaluation Criteria:</p>
                <ul className="text-xs text-white space-y-1">
                  <li>✓ Does the code work?</li>
                  <li>✓ Is it readable?</li>
                  <li>✓ Edge cases covered?</li>
                </ul>
              </div>
              <div className="bg-blue-900/30 p-3 rounded-lg border border-blue-400">
                <div className="flex items-center gap-2 mb-1">
                  <IconSolid name="info-circle" className="text-blue-400" />
                  <p className="text-xs font-semibold text-blue-300">Show Details</p>
                </div>
                <p className="text-xs text-white/80">
                  Complete task description with example prompts
                </p>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 13: Advanced Prompt Techniques
    // ============================================
    {
      id: 13,
      component: () => (
        <StandardContentSlide
          title="Advanced Prompt Techniques"
          subtitle="From Zero-Shot to Chain-of-Thought"
          background="light"
        >
          <div className="grid grid-cols-2 gap-6 mb-6">
            <FeatureCard
              title="1. Zero-Shot"
              description="Direct task without examples"
              items={[
                'Simplest form',
                'For clear, standardized tasks',
                'Example: "Create a TypeScript class for User"',
              ]}
            />
            <FeatureCard
              title="2. Few-Shot"
              description="Task with 1-3 examples"
              items={[
                'Show desired format/style',
                'For consistent results',
                'Example: "Like these 2 functions, create..."',
              ]}
            />
            <FeatureCard
              title="3. Chain-of-Thought"
              description="Step-by-step problem solving"
              items={[
                'Break down complex tasks',
                '"Think aloud" with the LLM',
                'Example: "Explain each step when refactoring"',
              ]}
            />
            <FeatureCard
              title="4. Role-Based"
              description="Define a role"
              items={[
                'LLM takes expert perspective',
                'For specialized tasks',
                'Example: "As a Senior Architect, evaluate..."',
              ]}
            />
          </div>

          <InfoBox variant="tip">
            <strong>Meta-Prompting:</strong> Let the LLM generate the perfect prompt for your task!
            <p className="mt-2 text-sm opacity-90">
              Example: "Create an optimal prompt for: [your task]"
            </p>
          </InfoBox>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 14: Prompt Engineering - Interactive Exercise
    // ============================================
    {
      id: 14,
      component: () => (
        <StandardContentSlide
          title="💭 Interactive Exercise: Prompt Improvement"
          subtitle="From bad to good prompts"
          background="secondary"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-3">Group Work (15 Minutes)</h3>
              <p className="mb-4">Improve these bad prompts together:</p>
            </InfoBox>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                  <p className="text-sm font-bold text-red-300 mb-2">❌ Bad Prompt #1</p>
                  <code className="text-sm">"Create a sorting function"</code>
                </div>
                <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                  <p className="text-sm font-bold text-red-300 mb-2">❌ Bad Prompt #2</p>
                  <code className="text-sm">"Fix the bug in the code"</code>
                </div>
                <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                  <p className="text-sm font-bold text-red-300 mb-2">❌ Bad Prompt #3</p>
                  <code className="text-sm">"Write tests"</code>
                </div>
                <div className="w-full bg-blue-600/30 p-3 rounded-lg border border-blue-400">
                  <div className="flex items-center justify-center gap-2">
                    <IconSolid name="info-circle" className="text-blue-300" />
                    <p className="text-sm font-semibold text-blue-200">Show Detailed Solution</p>
                  </div>
                </div>
              </div>
              
              <FeatureCard
                icon={<IconSolid name="lightbulb" className="text-yellow-400" />}
                title="Improvement Checklist"
                items={[
                  '✓ Specific task defined?',
                  '✓ Programming language mentioned?',
                  '✓ Context provided?',
                  '✓ Desired format described?',
                  '✓ Constraints/Requirements mentioned?',
                  '✓ Examples added?',
                ]}
                variant="glass"
              />
            </div>

            <div className="flex gap-4">
              <FeatureCard
                icon={<IconSolid name="users" />}
                title="10 Min"
                description="Group work"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="comments" />}
                title="5 Min"
                description="Presentation"
                variant="glass"
              />
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // SLIDE 15: Prompt Engineering - Practical Task (COMPRESSED)
    // ============================================
    {
      id: 15,
      component: () => (
        <StandardContentSlide
          title="✏️ Practical Exercise 3"
          subtitle="Prompt Engineering in Real-World Use"
          background="primary"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-3">📝 Task (20 Minutes)</h3>
              <p className="mb-4">
                Use ChatGPT/Claude with optimized prompts for these scenarios:
              </p>
            </InfoBox>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <FeatureCard
                  title="Scenario 1: Code Review"
                  description="⭐ Beginner"
                  items={[
                    'Prompt for code review with best practices',
                    'Concrete improvement suggestions',
                  ]}
                  variant="glass"
                />
                <FeatureCard
                  title="Scenario 2: Refactoring"
                  description="⭐⭐ Advanced"
                  items={[
                    'Modernize legacy code with clear goals',
                    'Request step-by-step guide',
                  ]}
                  variant="glass"
                />
              </div>
              <div className="space-y-4">
                <FeatureCard
                  title="Scenario 3: Architecture"
                  description="⭐⭐⭐ Expert"
                  items={[
                    'Microservice architecture with constraints',
                    'Including diagram suggestion',
                  ]}
                  variant="glass"
                />
                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <IconSolid name="info-circle" className="text-blue-400" />
                    <p className="text-sm font-semibold text-green-400">Details & Example Prompts</p>
                  </div>
                  <ul className="text-xs text-white/80 space-y-1">
                    <li>✓ Use Few-Shot with examples</li>
                    <li>✓ Define LLM's role</li>
                    <li>✓ Specify explicit format</li>
                    <li>✓ Iterate based on output</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // BLOCK 2: AI-CODING-TOOLS
    // ============================================

    // SLIDE 16: Section Divider - Block 2
    {
      id: 16,
      component: () => (
        <StandardContentSlide
          title="Block 2"
          subtitle="AI-Coding-Tools"
          background="secondary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🛠️</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">AI-Coding-Tools</h2>
              <p className="text-2xl text-white opacity-90">
                Market Overview and Tool Comparison
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">60 min</p>
                  <p className="text-sm text-white opacity-75">Duration</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">6</p>
                  <p className="text-sm text-white opacity-75">Tools</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">1</p>
                  <p className="text-sm text-white opacity-75">Demo</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 17: AI Tools - Overview
    {
      id: 17,
      component: () => (
        <StandardGridSlide
          title="AI-Coding-Tools - Overview"
          subtitle="The most important tools compared"
          columns={3}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="github" className="text-gray-900" />}
              title="GitHub Copilot"
              description="The Market Leader"
              items={['VS Code Integration', 'Code Completion', 'Chat Interface', 'Enterprise Support']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="edit" className="text-blue-600" />}
              title="Cursor"
              description="The Innovator"
              items={['Native AI IDE', 'Composer Mode', 'Codebase-wide', 'Multi-file Edits']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="robot" className="text-orange-600" />}
              title="Claude (Anthropic)"
              description="The Architect"
              items={['Artifacts', '200K Context', 'Code Analysis', 'Reasoning']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="terminal" className="text-green-600" />}
              title="Cline"
              description="The Autonomous"
              items={['VS Code Extension', 'Autonomous Coding', 'Terminal Access', 'File Operations']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="wind" className="text-cyan-600" />}
              title="Windsurf"
              description="The Rising Star"
              items={['Flow Mode', 'Multi-file Edits', 'Codeium-backed', 'Fast Performance']}
              variant="glass"
            />,
            <FeatureCard
              icon={<IconSolid name="building" className="text-purple-600" />}
              title="Enterprise Options"
              description="For Companies"
              items={['Amazon CodeWhisperer', 'Tabnine', 'Codeium', 'On-Premise Solutions']}
              variant="glass"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 18: Tool Comparison - Detailed
    {
      id: 18,
      component: () => (
        <StandardContentSlide
          title="Tool Comparison: Top 4 in Detail"
          subtitle="Feature Matrix and Use Cases"
          background="light"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="font-bold bg-gray-800 text-white p-3 rounded">Feature</div>
              <div className="font-bold bg-gray-700 text-white p-3 rounded text-center">Copilot</div>
              <div className="font-bold bg-blue-700 text-white p-3 rounded text-center">Cursor</div>
              <div className="font-bold bg-orange-700 text-white p-3 rounded text-center">Claude</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Code Completion</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Context Understanding</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Multi-File Edits</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">+</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">IDE Integration</div>
              <div className="bg-green-100 text-green-800 p-3 rounded text-center font-bold">+++</div>
              <div className="bg-yellow-100 text-yellow-800 p-3 rounded text-center font-bold">++</div>
              <div className="bg-red-100 text-red-800 p-3 rounded text-center font-bold">+</div>
              
              <div className="bg-gray-100 text-gray-900 p-3 rounded font-medium">Price/Month</div>
              <div className="bg-blue-100 text-blue-800 p-3 rounded text-center font-semibold">$10-39</div>
              <div className="bg-blue-100 text-blue-800 p-3 rounded text-center font-semibold">$20-200</div>
              <div className="bg-blue-100 text-blue-800 p-3 rounded text-center font-semibold">$20-200</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <InfoBox variant="success">
                <strong>🎯 Recommendation for Beginners:</strong> GitHub Copilot - Easy start, good integration
              </InfoBox>
              <InfoBox variant="info">
                <strong>🚀 Recommendation for Advanced:</strong> Cursor - Best features for complex projects
              </InfoBox>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 19: Interactive - Tool Selection
    {
      id: 19,
      component: () => (
        <StandardContentSlide
          title="💭 Interactive Discussion: Tool Selection"
          subtitle="Which tool for which scenario?"
          background="secondary"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-blue-900/40 p-4 rounded-lg border border-blue-400">
                  <h3 className="font-bold text-blue-300 mb-2">📝 Scenario 1: Startup</h3>
                  <p className="text-sm text-white">
                    Small team (5 devs), rapid iteration, limited budget
                  </p>
                  <p className="text-xs text-blue-200 mt-2">Which tool would you choose? Why?</p>
                </div>

                <div className="bg-green-900/40 p-4 rounded-lg border border-green-400">
                  <h3 className="font-bold text-green-300 mb-2">🏢 Scenario 2: Enterprise</h3>
                  <p className="text-sm text-white">
                    100+ developers, compliance requirements, on-premise preferred
                  </p>
                  <p className="text-xs text-green-200 mt-2">What are the criteria?</p>
                </div>

                <div className="bg-purple-900/40 p-4 rounded-lg border border-purple-400">
                  <h3 className="font-bold text-purple-300 mb-2">🔬 Scenario 3: Legacy</h3>
                  <p className="text-sm text-white">
                    Large legacy project, refactoring needed, complex codebase
                  </p>
                  <p className="text-xs text-purple-200 mt-2">Which features are important?</p>
                </div>
              </div>

              <FeatureCard
                icon={<IconSolid name="clipboard-list" className="text-yellow-400" />}
                title="Discussion Guide"
                items={[
                  '1. Budget & Cost',
                  '2. Team Size & Skill Level',
                  '3. Technology Stack',
                  '4. Security Requirements',
                  '5. Integration with existing setup',
                  '6. Learning curve & Onboarding',
                ]}
                variant="glass"
              />
            </div>

            <div className="flex gap-4">
              <FeatureCard
                icon={<IconSolid name="users" />}
                title="Groups"
                description="3-4 People"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="clock" />}
                title="10 Min"
                description="Discussion"
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="comments" />}
                title="5 Min"
                description="Presentation"
                variant="glass"
              />
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 20: Practical Task - Tool Setup (COMPRESSED)
    {
      id: 20,
      component: () => (
        <StandardContentSlide
          title="✏️ Practical Exercise 4"
          subtitle="AI Tool Installation & First Steps"
          background="primary"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-3">🔧 Task (30 Minutes)</h3>
              <p>Install and test an AI coding tool of your choice</p>
            </InfoBox>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <FeatureCard
                  title="Phase 1: Installation (10 Min)"
                  items={[
                    'Select & install tool',
                    'Create account & configure',
                  ]}
                  variant="glass"
                />

                <FeatureCard
                  title="Phase 2: Basic Test (10 Min)"
                  items={[
                    'Try code completion',
                    'Test chat interface & shortcuts',
                  ]}
                  variant="glass"
                />
              </div>

              <div className="space-y-4">
                <FeatureCard
                  title="Phase 3: Practical Test (10 Min)"
                  items={[
                    'Refactor your own code',
                    'Generate & document tests',
                  ]}
                  variant="glass"
                />

                <div className="bg-gray-900 p-4 rounded-lg border border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <IconSolid name="info-circle" className="text-blue-400" />
                    <p className="text-sm font-bold text-green-400">📋 Detailed Guide</p>
                  </div>
                  <div className="space-y-2 text-xs text-white/80">
                    <div className="bg-gray-800 p-2 rounded">
                      <code>// Generate a function to validate email addresses</code>
                    </div>
                    <div className="bg-gray-800 p-2 rounded">
                      <code>// Refactor this class to TypeScript interfaces</code>
                    </div>
                    <div className="bg-gray-800 p-2 rounded">
                      <code>// Write unit tests for this API route</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // BLOCK 3: COPILOT DEEP DIVE (Slides 21-30)
    // ============================================

    // SLIDE 21: Section Divider - Block 3
    {
      id: 21,
      component: () => (
        <StandardContentSlide
          title="Block 3"
          subtitle="Copilot Deep Dive"
          background="primary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🤖</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">Copilot Deep Dive</h2>
              <p className="text-2xl text-white opacity-90">
                Hands-on Workshop with Real Projects
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">90 min</p>
                  <p className="text-sm text-white opacity-75">Duration</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">2</p>
                  <p className="text-sm text-white opacity-75">Workshops</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">Live</p>
                  <p className="text-sm text-white opacity-75">Coding</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 22: Copilot Workflows
    {
      id: 22,
      component: () => (
        <StandardContentSlide
          title="GitHub Copilot - Core Workflows"
          subtitle="3 Essential Work Patterns for Maximum Productivity"
          background="light"
        >
          <div className="grid grid-cols-3 gap-6">
            <FeatureCard
              icon={<IconSolid name="bolt" className="text-blue-600" />}
              title="1. Code Completion"
              description="Inline suggestions while typing"
              items={[
                'Tab: Accept suggestion',
                'Alt+]: Next suggestion',
                'Complete functions',
                'Generate boilerplate',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="comments" className="text-green-600" />}
              title="2. Chat-based"
              description="Conversation in editor"
              items={[
                'Ctrl+I: Open chat',
                'Explain code',
                'Refactoring suggestions',
                'Generate bug fixes',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="vial" className="text-purple-600" />}
              title="3. Test-Driven"
              description="Tests first, then implementation"
              items={[
                'Write test',
                'Generate implementation',
                'Refactor safely',
                'Increase coverage',
              ]}
              variant="glass"
            />
          </div>

          <div className="grid grid-cols-4 gap-4 mt-6">
            <FeatureCard
              icon={<IconSolid name="rocket" />}
              title="2-3x"
              description="Faster development"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="brain" />}
              title="Focus"
              description="More time for architecture"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="layer-group" />}
              title="Consistency"
              description="Uniform code style"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="check" />}
              title="Quality"
              description="Fewer bugs"
              variant="elevated"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 23: Copilot - Live Demo Ideas
    {
      id: 23,
      component: () => (
        <StandardContentSlide
          title="🎬 Live Demo Suggestions"
          subtitle="What you can try right now"
          background="secondary"
        >
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <FeatureCard
                title="Demo 1: Create API Route"
                description="⏱️ 5 Minutes"
                items={[
                  '1. Comment: "Create GET /api/users route"',
                  '2. Generate Express.js handler',
                  '3. Add error handling',
                  '4. Validation with Zod',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Demo 2: React Component"
                description="⏱️ 5 Minutes"
                items={[
                  '1. Comment: "UserCard component with props"',
                  '2. Generate TypeScript interface',
                  '3. Style component with Tailwind',
                  '4. Props destructuring',
                ]}
                variant="glass"
              />
            </div>
            <div className="space-y-4">
              <FeatureCard
                title="Demo 3: Write Tests"
                description="⏱️ 5 Minutes"
                items={[
                  '1. Select function',
                  '2. Chat: "Write unit tests for this"',
                  '3. Add edge cases',
                  '4. Run tests',
                ]}
                variant="glass"
              />
              <div className="bg-gray-900 p-4 rounded-lg">
                <p className="text-sm text-green-400 font-bold mb-2">💡 Pro Tips:</p>
                <ul className="text-xs text-white space-y-1">
                  <li>✓ Write descriptive comments</li>
                  <li>✓ Review multiple suggestions (Alt+])</li>
                  <li>✓ Use chat for complex questions</li>
                  <li>✓ Always review and understand code!</li>
                </ul>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 24: Workshop 1 - Part 1
    {
      id: 24,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 1: E-Commerce Product API"
          subtitle="Part 1: Setup & Structure (10 Min)"
          background="primary"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-2">🎯 Goal</h3>
              <p>Create REST API for product management with Express.js, TypeScript and Copilot</p>
            </InfoBox>

            <div className="grid grid-cols-2 gap-6">
              <FeatureCard
                title="Step 1: Initialize Project"
                items={[
                  'mkdir ecommerce-api && cd ecommerce-api',
                  'npm init -y',
                  'npm install express typescript @types/express',
                  'npm install -D ts-node nodemon',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Step 2: TypeScript Setup"
                items={[
                  'npx tsc --init',
                  'Adjust tsconfig.json',
                  'Add package.json scripts',
                  'Create src/ folder',
                ]}
                variant="glass"
              />
            </div>

            <div className="bg-gray-900 p-4 rounded-lg border border-green-600">
              <div className="flex items-center gap-2 mb-2">
                <IconSolid name="info-circle" className="text-green-400" />
                <p className="text-sm font-bold text-green-400">💬 Complete Workshop Guide</p>
              </div>
              <code className="text-xs text-white/80 block mb-1">
                // Create a basic Express TypeScript server with CORS and JSON middleware
              </code>
              <code className="text-xs text-white/80 block">
                // Add health check route at GET /health
              </code>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 25: Workshop 1 - Part 2
    {
      id: 25,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 1: E-Commerce Product API"
          subtitle="Part 2: Models & Routes (15 Min)"
          background="primary"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <FeatureCard
                  title="Step 3: Product Model"
                  items={[
                    'Define TypeScript Interface',
                    'id, name, price, description',
                    'In-Memory Array as DB',
                  ]}
                  variant="glass"
                />
                <div className="bg-gray-900 p-3 rounded-lg">
                  <p className="text-xs text-green-400 mb-2">💬 Prompt:</p>
                  <code className="text-xs text-white">
                    // Create Product interface with id (string), name, price (number), description, inStock (boolean)
                  </code>
                </div>
              </div>

              <div className="space-y-4">
                <FeatureCard
                  title="Step 4: CRUD Routes"
                  items={[
                    'GET /api/products - all',
                    'GET /api/products/:id - one',
                    'POST /api/products - new',
                    'PUT /api/products/:id - update',
                    'DELETE /api/products/:id - delete',
                  ]}
                  variant="glass"
                />
                <div className="bg-gray-900 p-3 rounded-lg border border-blue-600">
                  <div className="flex items-center gap-2 mb-2">
                    <IconSolid name="info-circle" className="text-blue-400" />
                    <p className="text-xs font-bold text-green-400">💬 Complete Prompts & Code</p>
                  </div>
                  <code className="text-xs text-white/80">
                    // Create Express router with CRUD operations for Product
                  </code>
                </div>
              </div>
            </div>

            <InfoBox variant="tip">
              <strong>Tip:</strong> Use Copilot Chat (Ctrl+I) to generate complete router files!
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 26: Workshop 1 - Part 3
    {
      id: 26,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 1: E-Commerce Product API"
          subtitle="Part 3: Validation & Testing (15 Min)"
          background="primary"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <FeatureCard
                title="Step 5: Validation with Zod"
                items={[
                  'npm install zod',
                  'Define schema for Product',
                  'Middleware for validation',
                  'Error handling',
                ]}
                variant="glass"
              />

              <FeatureCard
                title="Step 6: Write Tests"
                items={[
                  'npm install -D jest supertest @types/jest',
                  'Configure Jest',
                  'Tests for all routes',
                  'Run npm test',
                ]}
                variant="glass"
              />
            </div>

            <div className="bg-gray-900 border-2 border-green-600 p-4 rounded-lg">
              <div className="flex items-center justify-center gap-2 mb-2">
                <IconSolid name="info-circle" className="text-green-400" />
                <p className="text-green-400 font-semibold">💬 Complete Validation & Testing Prompts</p>
              </div>
              <div className="space-y-1 text-xs text-gray-300">
                <p>• Zod Schema with Constraints</p>
                <p>• Integration Tests with Supertest</p>
              </div>
            </div>

            <InfoBox variant="success">
              <strong>✅ Done!</strong> You've created a complete REST API with Copilot - TypeScript, Validation, Tests
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // BLOCK 4: ADVANCED PRACTICE & SECURITY
    // ============================================

    // SLIDE 27: Section Divider - Block 4
    {
      id: 27,
      component: () => (
        <StandardContentSlide
          title="Block 4"
          subtitle="Advanced Practice & Security"
          background="accent"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🔐</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">Advanced Practice & Security</h2>
              <p className="text-2xl text-white opacity-90">
                Legacy Code, Tests & Security
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">60 min</p>
                  <p className="text-sm text-white opacity-75">Duration</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">2</p>
                  <p className="text-sm text-white opacity-75">Workshops</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">Best Practices</p>
                  <p className="text-sm text-white opacity-75">Security</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 28: Workshop 2 - Legacy Code - Part 1
    {
      id: 28,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 2: Legacy Code Modernization"
          subtitle="Part 1: Callback Hell → Async/Await (15 Min)"
          background="light"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-2">🎯 Goal</h3>
              <p>Modernize old JavaScript code with Copilot</p>
            </InfoBox>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-red-900/30 p-4 rounded-lg border border-red-400">
                <p className="text-sm font-bold text-red-300 mb-2">❌ Before: Callback Hell</p>
                <pre className="text-xs text-white overflow-x-auto"><code>{`getUserData(userId, (err, user) => {
  if (err) return handleError(err);
  getOrders(user.id, (err, orders) => {
    if (err) return handleError(err);
    getProducts(orders, (err, products) => {
      if (err) return handleError(err);
      render(products);
    });
  });
});`}</code></pre>
              </div>

              <div className="space-y-4">
                <div className="bg-green-900/30 p-4 rounded-lg border border-green-400">
                  <p className="text-sm font-bold text-green-300 mb-2">✅ After: Async/Await</p>
                  <pre className="text-xs text-white overflow-x-auto"><code>{`try {
  const user = await getUserData(userId);
  const orders = await getOrders(user.id);
  const products = await getProducts(orders);
  render(products);
} catch (error) {
  handleError(error);
}`}</code></pre>
                </div>

                <div className="bg-gray-900 border-2 border-amber-600 p-3 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <IconSolid name="info-circle" className="text-amber-400" />
                    <p className="text-amber-400 font-semibold text-sm">🔧 Complete Modernization Guide</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 29: Workshop 2 - Part 2
    {
      id: 29,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 2: Legacy Code Modernization"
          subtitle="Part 2: Modern ES6+ Features (15 Min)"
          background="light"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FeatureCard
                title="var → const/let"
                items={[
                  'Block scope instead of function scope',
                  'Immutability where possible',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="function → Arrow Functions"
                items={[
                  'Lexical this binding',
                  'Shorter syntax',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Destructuring"
                items={[
                  'Object & Array destructuring',
                  'Default values',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Spread & Rest"
                items={[
                  'Spread Operator (...)',
                  'Object/Array Merging',
                ]}
                variant="glass"
              />
            </div>

            <div className="bg-gray-900 p-3 rounded-lg">
              <p className="text-sm text-green-400 mb-1">💬 Mega-Prompt for Modernization:</p>
              <code className="text-xs text-white block">
                Modernize this code: ES6+, const/let, arrow functions, destructuring, async/await, TypeScript types
              </code>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 30: Workshop 3 - Test Coverage
    {
      id: 30,
      component: () => (
        <StandardContentSlide
          title="🛠️ Workshop 3: Increase Test Coverage"
          subtitle="Automatic Test Generation with Copilot (20 Min)"
          background="primary"
        >
          <div className="space-y-6">
            <InfoBox variant="warning">
              <h3 className="text-xl font-bold mb-2">🎯 Goal</h3>
              <p>From 0% to 80%+ Test Coverage with Copilot</p>
            </InfoBox>

            <div className="grid grid-cols-3 gap-4">
              <FeatureCard
                title="Phase 1: Unit Tests"
                description="⏱️ 8 Min"
                items={[
                  'Mark function',
                  'Chat: "Write unit tests"',
                  'Add edge cases',
                  'Jest/Vitest setup',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Phase 2: Integration"
                description="⏱️ 7 Min"
                items={[
                  'Test API routes',
                  'Database mocks',
                  'Supertest for HTTP',
                  'Happy & Error path',
                ]}
                variant="glass"
              />
              <FeatureCard
                title="Phase 3: Coverage"
                description="⏱️ 5 Min"
                items={[
                  'npm test -- --coverage',
                  'Identify gaps',
                  'Generate missing tests',
                  'Configure threshold',
                ]}
                variant="glass"
              />
            </div>

            <div className="bg-gray-900 border-2 border-purple-600 p-4 rounded-lg">
              <div className="flex items-center justify-center gap-2 mb-2">
                <IconSolid name="info-circle" className="text-purple-400" />
                <p className="text-purple-400 font-semibold">🧪 Complete Test Generation Guide</p>
              </div>
              <div className="space-y-1 text-xs text-gray-300">
                <p>• Unit Tests with Edge Cases</p>
                <p>• Integration Tests with Mocks</p>
                <p>• Coverage Optimization</p>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // ============================================
    // BLOCK 5: CONTEXT ENGINEERING (Slides 31-37)
    // ============================================

    // SLIDE 31: Section Divider - Context Engineering
    {
      id: 31,
      component: () => (
        <StandardContentSlide
          title="Block 5"
          subtitle="Context Engineering"
          background="accent"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <div className="text-8xl">🎯</div>
            <div className="text-center space-y-6">
              <h2 className="text-5xl font-bold text-white">Context Engineering</h2>
              <p className="text-2xl text-white opacity-90">
                Systematic AI Development
              </p>
              <div className="flex gap-8 justify-center mt-8">
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">45 min</p>
                  <p className="text-sm text-white opacity-75">Duration</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">Framework</p>
                  <p className="text-sm text-white opacity-75">& Practice</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white">Enterprise</p>
                  <p className="text-sm text-white opacity-75">Ready</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },

    // SLIDE 32: What is Context Engineering?
    {
      id: 32,
      component: () => (
        <StandardContentSlide
          title="What is Context Engineering?"
          subtitle="From simple prompts to systematic AI development"
          background="light"
        >
          <div className="grid grid-cols-2 gap-6">
            <FeatureCard
              icon={<IconSolid name="lightbulb" className="text-yellow-600" />}
              title="Simple Prompting"
              description="Ad-hoc requests"
              items={[
                '❌ Vague formulations',
                '❌ Inconsistent results',
                '❌ Lots of trial & error',
                '❌ Hard to reproduce',
              ]}
            />
            <FeatureCard
              icon={<IconSolid name="rocket" className="text-green-600" />}
              title="Context Engineering"
              description="Systematic approach"
              items={[
                '✅ Structured communication',
                '✅ Consistent quality',
                '✅ Repeatable processes',
                '✅ Enterprise-ready',
              ]}
            />
          </div>

          <InfoBox variant="tip">
            <strong>💡 Definition:</strong> Context Engineering is the systematic methodology for structuring information and requirements for optimal AI interaction in professional development projects.
          </InfoBox>

          <div className="grid grid-cols-4 gap-4 mt-6">
            <FeatureCard
              icon={<IconSolid name="bullseye" className="text-primary-600" />}
              title="Clarity"
              description="Precise goals"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="sitemap" className="text-secondary-600" />}
              title="Structure"
              description="Systematic approach"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="database" className="text-accent-600" />}
              title="Context"
              description="Relevant info"
              variant="elevated"
            />
            <FeatureCard
              icon={<IconSolid name="sync-alt" className="text-green-600" />}
              title="Iteration"
              description="Continuous"
              variant="elevated"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 33: The Context Engineering Framework
    {
      id: 33,
      component: () => (
        <StandardContentSlide
          title="The Context Engineering Framework"
          subtitle="4-Step Process for successful AI interaction"
          background="primary"
        >
          <div className="bg-gray-900 text-cyan-400 p-6 rounded-lg font-mono text-sm mb-6">
            <div className="space-y-4">
              <div>
                <span className="text-green-400"># ROLE</span><br/>
                <span className="text-white">You are a Senior TypeScript Developer with 10+ years experience.<br/>
                Specialization: React, Node.js, Clean Code, Testing Best Practices</span>
              </div>
              <div>
                <span className="text-green-400"># CONTEXT</span><br/>
                <span className="text-white">Project: E-Commerce Product Management<br/>
                Stack: React 18, TypeScript, Tailwind CSS, Vite<br/>
                Requirements: Responsive, Accessibility (WCAG AA), Type-Safe</span>
              </div>
              <div>
                <span className="text-green-400"># REQUIREMENTS</span><br/>
                <span className="text-white">Feature: Product Catalog with Filter & Search<br/>
                - Grid Layout (3 columns desktop, 1 column mobile)<br/>
                - Search Bar with Debounce (300ms)<br/>
                - Category Filter (Dropdown)<br/>
                - Loading States & Error Handling</span>
              </div>
              <div>
                <span className="text-green-400"># DELIVERABLES</span><br/>
                <span className="text-white">Output: ProductCatalog.tsx Component<br/>
                - TypeScript Interfaces for Props & Data<br/>
                - Responsive Design (Mobile-First)<br/>
                - Unit Tests with Vitest<br/>
                - Inline Code Comments</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FeatureCard
              icon={<IconSolid name="times-circle" className="text-red-400" />}
              title="❌ Without Framework"
              items={[
                '"Make a product list"',
                'Vague, ambiguous',
                'Unpredictable results',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="check-circle" className="text-green-400" />}
              title="✅ With Framework"
              items={[
                'Structured & complete',
                'Clear success criteria',
                'First iteration often perfect',
              ]}
              variant="glass"
            />
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 34: Practical Exercise - Context Engineering
    {
      id: 34,
      component: () => (
        <StandardContentSlide
          title="✏️ Practical Exercise: Context Engineering"
          subtitle="Transform a bad prompt into a professional one (15 Min)"
          background="light"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-red-50 border-2 border-red-600 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-red-900 mb-4 flex items-center gap-2">
                  <IconSolid name="times-circle" className="text-red-600" />
                  ❌ Bad Prompt
                </h3>
                <div className="bg-red-100 p-4 rounded">
                  <code className="text-red-900 text-sm">
                    "Write an API for user management with login and stuff"
                  </code>
                </div>
                <ul className="mt-4 text-sm text-red-900 space-y-1">
                  <li>• No tech stack specified</li>
                  <li>• Vague requirements</li>
                  <li>• No security standards</li>
                  <li>• No success criteria</li>
                </ul>
              </div>

              <div className="bg-green-50 border-2 border-green-600 p-6 rounded-lg">
                <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2">
                  <IconSolid name="check-circle" className="text-green-600" />
                  ✅ Your Task
                </h3>
                <div className="text-green-900 space-y-3">
                  <p className="font-semibold">Use the Framework:</p>
                  <ol className="list-decimal ml-5 space-y-2 text-sm">
                    <li><strong>ROLE:</strong> Define expertise</li>
                    <li><strong>CONTEXT:</strong> Tech stack & business</li>
                    <li><strong>REQUIREMENTS:</strong> Functional & non-functional</li>
                    <li><strong>DELIVERABLES:</strong> Concrete outputs</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="bg-gray-900 border-2 border-blue-600 p-4 rounded-lg">
                <div className="flex items-center justify-center gap-2">
                  <IconSolid name="info-circle" className="text-blue-400" />
                  <p className="text-blue-400 font-semibold">💬 View Solution & Example Prompts</p>
                </div>
              </div>
            </div>

            <InfoBox variant="tip">
              <strong>💡 Tip:</strong> Start with business value, then technical details. Concrete metrics instead of vague descriptions!
            </InfoBox>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 35: Best Practices & Anti-Patterns
    {
      id: 35,
      component: () => (
        <StandardGridSlide
          title="Best Practices & Anti-Patterns"
          subtitle="Avoid common mistakes, ensure quality"
          columns={2}
          background="secondary"
          items={[
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white mb-4">✅ Best Practices</h3>
              <FeatureCard
                icon={<IconSolid name="bullseye" className="text-green-400" />}
                title="Clear Goal Definition"
                items={[
                  'Measurable success criteria',
                  'Concrete use cases',
                  'Define acceptance criteria',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="shield-check" className="text-blue-400" />}
                title="Security from the Start"
                items={[
                  'Include GDPR/Compliance',
                  'Explicit security requirements',
                  'No secrets in prompts',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="sync-alt" className="text-purple-400" />}
                title="Iterative Approach"
                items={[
                  'Start small, expand incrementally',
                  'Build feedback loops',
                  'Continuous improvement',
                ]}
                variant="glass"
              />
            </div>,
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white mb-4">❌ Anti-Patterns</h3>
              <FeatureCard
                icon={<IconSolid name="exclamation-triangle" className="text-red-400" />}
                title="Scope Creep"
                items={[
                  '❌ "Create a complete CRM..."',
                  '✅ Break tasks into small steps',
                  '✅ Use MVP approach',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="ban" className="text-orange-400" />}
                title="Missing Constraints"
                items={[
                  '❌ No technical boundaries',
                  '✅ Define budget, time, tech stack',
                  '✅ Specify performance goals',
                ]}
                variant="glass"
              />
              <FeatureCard
                icon={<IconSolid name="question-circle" className="text-yellow-400" />}
                title="Vague Requirements"
                items={[
                  '❌ "Make it better, faster"',
                  '✅ Concrete metrics (e.g. <2s LCP)',
                  '✅ Apply SMART criteria',
                ]}
                variant="glass"
              />
            </div>,
          ]}
        />
      ),
      transition: 'slide',
    },

    // SLIDE 36: Context Engineering in Practice
    {
      id: 36,
      component: () => (
        <StandardContentSlide
          title="Context Engineering in Practice"
          subtitle="How to establish it in your team"
          background="light"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <FeatureCard
                icon={<IconSolid name="users" className="text-primary-600" />}
                title="1. Team Templates"
                description="Reusable prompt templates"
                items={[
                  'Frontend Development',
                  'Backend/API',
                  'Testing & QA',
                  'Code Review',
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="book" className="text-secondary-600" />}
                title="2. Documentation"
                description="Capture best practices"
                items={[
                  'Build prompt library',
                  'Share successful patterns',
                  'Lessons learned',
                  'Team guidelines',
                ]}
                variant="elevated"
              />
              <FeatureCard
                icon={<IconSolid name="chart-line" className="text-accent-600" />}
                title="3. Continuous Improvement"
                description="Metrics & Feedback"
                items={[
                  'Measure success rate',
                  'Track iteration counts',
                  'Evaluate quality',
                  'Gather team feedback',
                ]}
                variant="elevated"
              />
            </div>

            <InfoBox variant="success">
              <h4 className="font-bold mb-3">🎯 Quick Start for Your Team:</h4>
              <ol className="space-y-2 text-sm">
                <li>1. <strong>Create 3-5 standard templates</strong> for your most common tasks</li>
                <li>2. <strong>Define quality criteria</strong> (what is "good enough"?)</li>
                <li>3. <strong>Establish review processes</strong> for AI-generated code</li>
                <li>4. <strong>Measure the impact</strong> (time saved, quality improved)</li>
                <li>5. <strong>Iterate based on feedback</strong> from the team</li>
              </ol>
            </InfoBox>

            <div className="bg-gradient-to-r from-primary-500/20 to-secondary-500/20 border-2 border-primary-500/50 rounded-xl p-6">
              <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                <IconSolid name="rocket" className="text-primary-600" />
                Next Steps
              </h3>
              <div className="grid grid-cols-2 gap-4 text-lg">
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Use Context Engineering in your next Copilot sessions</div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Create team templates for recurring tasks</div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Share successful prompts with the team</div>
                </div>
                <div className="flex items-start gap-3">
                  <IconSolid name="check" className="text-green-600 mt-1" />
                  <div>Measure improvement in your development speed</div>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // SLIDE 37: Security Best Practices
    {
      id: 37,
      component: () => (
        <StandardGridSlide
          title="Security & Best Practices"
          subtitle="Use AI tools safely and effectively"
          columns={2}
          background="light"
          items={[
            <FeatureCard
              icon={<IconSolid name="shield-alt" className="text-red-600" />}
              title="🔒 Security Concerns"
              items={[
                '❌ No secrets/API keys in prompts',
                '❌ No sensitive data sharing',
                '❌ Observe compliance requirements',
                '✅ Use enterprise versions',
                '✅ Always review code',
              ]}
              variant="elevated"
            />,
            <FeatureCard
              icon={<IconSolid name="check-circle" className="text-green-600" />}
              title="✅ Best Practices"
              items={[
                '✓ Understand code, don\'t blindly copy',
                '✓ Tests for AI-generated code',
                '✓ Run security scans',
                '✓ Check license compliance',
                '✓ Establish team guidelines',
              ]}
              variant="elevated"
            />,
          ]}
        />
      ),
      transition: 'slide',
    },

    // Summary slide at the end
    {
      id: 38,
      component: () => (
        <StandardContentSlide
          title="📚 Day 1 Summary"
          subtitle="What you learned today"
          background="primary"
        >
          <div className="grid grid-cols-2 gap-6">
            <FeatureCard
              icon={<IconSolid name="brain" className="text-blue-400" />}
              title="Block 1: AI Fundamentals"
              items={[
                'AI Landscape: AI → ML → DL → GenAI',
                'LLM Architecture & How it works',
                'Prompt Engineering Basics',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="tools" className="text-green-400" />}
              title="Block 2: AI Coding Tools"
              items={[
                'Tool Comparison & Selection',
                'Copilot, Cursor, Claude, Cline',
                'Best practices & Use cases',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="code" className="text-purple-400" />}
              title="Block 3 & 4: Hands-on"
              items={[
                'GitHub Copilot Workflows',
                'E-Commerce API Workshop',
                'Legacy Code Modernization',
              ]}
              variant="glass"
            />
            <FeatureCard
              icon={<IconSolid name="bullseye" className="text-orange-400" />}
              title="Block 5: Context Engineering"
              items={[
                'Systematic AI Development',
                'Framework & Best Practices',
                'Enterprise Implementation',
              ]}
              variant="glass"
            />
          </div>

          <InfoBox variant="success" className="mt-6">
            <strong>Next Steps:</strong> Apply what you learned! Start with small projects, build your prompt library, and share experiences with your team.
          </InfoBox>

          <div className="text-center mt-6 text-white/60">
            Thank you for participating! Questions?
          </div>
        </StandardContentSlide>
      ),
      transition: 'slide',
    },

    // Q&A Slide
    {
      id: 39,
      component: () => (
        <StandardContentSlide
          title="Questions & Discussion"
          background="primary"
        >
          <div className="flex flex-col items-center justify-center h-full space-y-12">
            <IconSolid name="comments" className="w-40 h-40 text-white/80" />
            <div className="text-center space-y-6">
              <h2 className="text-6xl font-bold text-white">Questions?</h2>
              <p className="text-2xl text-white/70 max-w-2xl">
                We're here to help you get started with AI-powered development
              </p>
              <div className="flex gap-8 justify-center pt-8">
                <div className="text-center">
                  <IconSolid name="envelope" className="w-12 h-12 mx-auto mb-2 text-white/60" />
                  <p className="text-white/80 text-sm">Email us</p>
                </div>
                <div className="text-center">
                  <IconSolid name="link" className="w-12 h-12 mx-auto mb-2 text-white/60" />
                  <p className="text-white/80 text-sm">Resources online</p>
                </div>
                <div className="text-center">
                  <IconSolid name="users" className="w-12 h-12 mx-auto mb-2 text-white/60" />
                  <p className="text-white/80 text-sm">Join community</p>
                </div>
              </div>
            </div>
          </div>
        </StandardContentSlide>
      ),
      transition: 'fade',
    },
  ],
};

export default developerTrainingDay1;
